import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { performLogout, showSessionExpiredModal } from '../slices/authSlice';
import authFetch from '../utils/authFetch';
import { normalizeScoreBreakdown } from '../utils/scoreBreakdown';
import { 
    CloudArrowUpIcon, 
    DocumentTextIcon, 
    ExclamationTriangleIcon,
    CheckCircleIcon,
    SparklesIcon,
    ChartBarIcon,
    ClipboardDocumentIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import UserCredits from './UserCredits';
import AiAssistantButton from './AiAssistantButton';
import ModalReview from './ModalReview';
import ModalUpsell from './ModalUpsell';
import OnboardingModal from './OnboardingModal';
import {
    trackCVUpload,
    trackCVAnalysis,
    trackError,
    trackCreditsInsufficient,
    trackCvAnalysisFailed,
    trackSubscriptionCtaClick,
} from '../utils/analytics';

const STEPS = [
    { label: 'Extraction du texte', duration: 2500 },
    { label: 'Analyse par secteur', duration: 3500 },
    { label: 'Génération des conseils', duration: 3500 },
    { label: 'Calcul du score', duration: 2000 },
];

const JOB_STEPS = [
    { label: 'Extraction du texte', duration: 2000 },
    { label: 'Lecture de l\'offre', duration: 2500 },
    { label: 'Comparaison CV / offre', duration: 4000 },
    { label: 'Génération des conseils ciblés', duration: 3000 },
];

const UnlockReportCta = ({ title = 'Débloquez le rapport complet', hasPaid = false, onReanalyze }) => (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200 text-center">
        <h4 className="text-lg font-bold text-gray-800 mb-2">{title}</h4>
        <p className="text-sm text-gray-600 mb-4">
            {hasPaid
                ? 'L’aperçu a été calculé sans IA. Importez à nouveau votre CV pour le rapport complet, l’ATS et le CV restructuré.'
                : 'Dès 5 €, importez à nouveau votre CV : l’IA génère le rapport complet, l’ATS et le CV restructuré.'}
        </p>
        <ul className="text-sm text-gray-700 text-left max-w-md mx-auto mb-5 space-y-1">
            <li>Détail du score sur 5 critères</li>
            <li>Toutes les recommandations</li>
            <li>Mots-clés ATS présents et manquants</li>
            <li>CV restructuré à copier ou télécharger</li>
        </ul>
        {hasPaid ? (
            <button
                type="button"
                onClick={onReanalyze}
                className="inline-block bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl"
            >
                Importer le CV pour l’analyse IA
            </button>
        ) : (
            <Link
                to="/subscription"
                onClick={() => trackSubscriptionCtaClick('limited_analysis_unlock')}
                className="inline-block bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl"
            >
                Rapport complet + CV restructuré — 5 €
            </Link>
        )}
    </div>
);

const AnalysisProgressBar = ({ isJobMatch }) => {
    const steps = isJobMatch ? JOB_STEPS : STEPS;
    const [currentStep, setCurrentStep] = React.useState(0);
    const [progress, setProgress] = React.useState(0);

    React.useEffect(() => {
        let stepIndex = 0;
        let progressVal = 0;
        const totalDuration = steps.reduce((s, st) => s + st.duration, 0);
        let elapsed = 0;

        const tick = setInterval(() => {
            elapsed += 100;
            const targetProgress = Math.min(92, Math.round((elapsed / totalDuration) * 100));
            setProgress(targetProgress);

            // Avancer d'étape
            let cumul = 0;
            for (let i = 0; i < steps.length; i++) {
                cumul += steps[i].duration;
                if (elapsed < cumul) { setCurrentStep(i); break; }
            }
        }, 100);

        return () => clearInterval(tick);
    }, [steps]);

    return (
        <div className="py-10 px-4">
            <div className="flex items-center justify-center mb-6">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mr-4"></div>
                <div>
                    <p className="text-gray-800 font-semibold text-lg">
                        {steps[currentStep]?.label}...
                    </p>
                    <p className="text-gray-400 text-sm">Cela prend environ 15 secondes</p>
                </div>
            </div>

            {/* Barre de progression */}
            <div className="w-full bg-gray-100 rounded-full h-2.5 mb-5 overflow-hidden">
                <div
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-200 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Étapes */}
            <div className="flex justify-between">
                {steps.map((step, i) => (
                    <div key={step.label} className="flex flex-col items-center gap-1" style={{ width: `${100 / steps.length}%` }}>
                        <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                            i < currentStep ? 'bg-green-500' :
                            i === currentStep ? 'bg-blue-600 ring-2 ring-blue-200' :
                            'bg-gray-200'
                        }`} />
                        <p className={`text-center text-xs leading-tight hidden sm:block ${
                            i <= currentStep ? 'text-gray-600' : 'text-gray-300'
                        }`}>{step.label}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const CvUploader = () => {
    const dispatch = useDispatch();
    const [file, setFile] = useState(null);
    const [sector, setSector] = useState('');
    const [feedback, setFeedback] = useState('');
    const [score, setScore] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [insufficientCredits, setInsufficientCredits] = useState(false);
    const [reloadCreditsTrigger, setReloadCreditsTrigger] = useState(0);
    const [isLimited, setIsLimited] = useState(false);
    const [formattedCv, setFormattedCv] = useState('');
    const [analysisId, setAnalysisId] = useState(null);
    const [completeCv, setCompleteCv] = useState('');
    const [isGeneratingCompleteCv, setIsGeneratingCompleteCv] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [cvWithPlaceholders, setCvWithPlaceholders] = useState('');
    const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showUpsellModal, setShowUpsellModal] = useState(false);
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);
    const [isFormattedCvExpanded, setIsFormattedCvExpanded] = useState(false);
    const [isCompleteCvExpanded, setIsCompleteCvExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('score');
    const [scoreBreakdown, setScoreBreakdown] = useState(null);
    const [pointsForts, setPointsForts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [motsClesPresents, setMotsClesPresents] = useState([]);
    const [motsClesManquants, setMotsClesManquants] = useState([]);
    const [jobOffer, setJobOffer] = useState('');
    const [isJobMatch, setIsJobMatch] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const location = useLocation();
    const wantsUnlock = new URLSearchParams(location.search).get('unlocked') === '1';

    const displayScoreBreakdown = useMemo(
        () => normalizeScoreBreakdown(scoreBreakdown, score),
        [scoreBreakdown, score]
    );

    // Référence persistante pour le fichier (survit aux re-renders)
    const fileRef = useRef(null);
    const skipRestoreRef = useRef(false);
    // Référence pour le champ actuel du formulaire
    const currentFieldRef = useRef(null);
    
    // Variable globale pour stocker le fichier (survit aux re-renders)
    window.currentSelectedFile = null;
    
    // Optimiser les useSelector pour éviter les re-renders constants
    const authState = useSelector((state) => state.auth);
    const token = useMemo(() => authState.token, [authState.token]);
    const userId = useMemo(() => authState.user?.id, [authState.user?.id]);
    const userCredits = useMemo(() => parseFloat(authState.user?.credits || 0), [authState.user?.credits]);
    const userHasPaid = Boolean(authState.user?.hasPaid);

    // La validation du token reste active sur cette page.
    // Elle est désactivée uniquement pendant l'upload (dans handleSubmit).

    // Focus sur le champ actuel quand l'index change
    useEffect(() => {
        if (currentFieldRef.current && missingFields.length > 0) {
            // Petit délai pour s'assurer que le DOM est mis à jour
            setTimeout(() => {
                currentFieldRef.current?.focus();
            }, 100);
        }
    }, [currentFieldIndex, missingFields.length]);

    // Afficher l'onboarding à la première visite de l'utilisateur
    useEffect(() => {
        if (!userId) return;
        const key = `cv_onboarded_${userId}`;
        if (!localStorage.getItem(key)) {
            setShowOnboarding(true);
        }
    }, [userId]);

    const handleCloseOnboarding = () => {
        setShowOnboarding(false);
        if (userId) localStorage.setItem(`cv_onboarded_${userId}`, '1');
    };

    // Empêcher l'ouverture de fichiers par drag & drop sur toute la page
    useEffect(() => {
        const handleGlobalDragOver = (e) => {
            e.preventDefault();
        };
        const handleGlobalDrop = (e) => {
            e.preventDefault();
        };

        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('drop', handleGlobalDrop);

        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('drop', handleGlobalDrop);
        };
    }, []);

    const persistAnalysis = useCallback((analysisData) => {
        localStorage.setItem('lastAnalysis', JSON.stringify({
            ...analysisData,
            timestamp: analysisData.timestamp || Date.now(),
        }));
    }, []);

    const applyPayload = useCallback((analysisData, { persist = false } = {}) => {
        const limited = Boolean(analysisData.isLimited);
        setFeedback(analysisData.feedback || '');
        setScore(analysisData.score);
        setScoreBreakdown(normalizeScoreBreakdown(analysisData.scoreBreakdown || null, analysisData.score));
        setPointsForts(analysisData.pointsForts || []);
        setSuggestions(analysisData.suggestions || []);
        setMotsClesPresents(analysisData.motsClesPresents || []);
        setMotsClesManquants(analysisData.motsClesManquants || []);
        setIsJobMatch(analysisData.isJobMatch || false);
        setAnalysisId(analysisData.analysisId || analysisData.id || null);
        setFormattedCv(limited ? '' : (analysisData.cvText || analysisData.formattedCv || ''));
        setIsLimited(limited);
        setShowAllSuggestions(!limited);
        setIsComplete(true);
        if (!limited) {
            skipRestoreRef.current = true;
        }
        if (persist) {
            persistAnalysis({
                ...analysisData,
                analysisId: analysisData.analysisId || analysisData.id || null,
                isLimited: limited,
            });
        }
    }, [persistAnalysis]);

    // Restaurer la dernière analyse au chargement uniquement (pas après "Ré-analyser")
    useEffect(() => {
        if (skipRestoreRef.current || isComplete) return;

        const savedAnalysis = localStorage.getItem('lastAnalysis');
        if (savedAnalysis) {
            try {
                const analysisData = JSON.parse(savedAnalysis);
                const isRecent = Date.now() - analysisData.timestamp < 7 * 24 * 3600000;
                if (isRecent) {
                    applyPayload(analysisData);
                    return;
                }
                localStorage.removeItem('lastAnalysis');
            } catch (error) {
                localStorage.removeItem('lastAnalysis');
            }
        }

        if (!userId || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/history/${userId}`);
                if (!response.ok || cancelled || skipRestoreRef.current) return;
                const data = await response.json();
                const latest = data.analyses?.[0];
                if (!latest || cancelled || skipRestoreRef.current) return;
                let parsed = {};
                try {
                    parsed = JSON.parse(latest.feedback || '{}');
                } catch {
                    parsed = {};
                }
                applyPayload({
                    feedback: latest.feedback,
                    score: latest.score,
                    scoreBreakdown: parsed.scoreBreakdown,
                    pointsForts: parsed.pointsForts || [],
                    suggestions: parsed.suggestions || [],
                    motsClesPresents: parsed.motsClesPresents || [],
                    motsClesManquants: parsed.motsClesManquants || [],
                    analysisId: latest.id,
                    sector: latest.sector,
                    fileName: latest.filename,
                    isLimited: latest.isLimited || false,
                });
            } catch {
                // Pas bloquant : l'utilisateur peut relancer une analyse
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isComplete, userId, token, applyPayload]);

    // Après paiement : débloque l'analyse déjà stockée, sans relancer OpenAI
    useEffect(() => {
        if (!userId) return;
        if (!wantsUnlock && !userHasPaid) return;
        if (analysisId && !isLimited) return;

        let cancelled = false;
        (async () => {
            let targetId = analysisId;
            if (!targetId) {
                try {
                    const historyRes = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/history/${userId}`);
                    if (!historyRes.ok || cancelled) return;
                    const historyData = await historyRes.json();
                    targetId = historyData.analyses?.[0]?.id;
                } catch {
                    return;
                }
            }
            if (!targetId || cancelled) return;

            for (let attempt = 0; attempt < 4 && !cancelled; attempt++) {
                try {
                    const response = await authFetch(
                        `https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/analysis/${targetId}`,
                        { headers: { 'X-User-Id': String(userId) } }
                    );
                    if (!response.ok || cancelled) return;
                    const data = await response.json();
                    if (cancelled) return;
                    if (data && !data.isLimited) {
                        applyPayload({ ...data, analysisId: data.id }, { persist: true });
                        setReloadCreditsTrigger((n) => n + 1);
                        return;
                    }
                } catch {
                    // webhook Stripe parfois un peu en retard
                }
                await new Promise((resolve) => setTimeout(resolve, 1500));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [userId, analysisId, wantsUnlock, userHasPaid, isLimited, applyPayload]);


    const sectors = [
        { value: 'informatique', label: 'Informatique' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' },
        { value: 'ressources_humaines', label: 'Ressources Humaines' },
        { value: 'santé', label: 'Santé' },
        { value: 'autre', label: 'Autre' }
    ];


    // Fonction stable pour valider et traiter un fichier
    const processFile = useCallback((selectedFile) => {
        // Validation de la taille
        const maxSize = 15 * 1024 * 1024; // 15 Mo
        if (selectedFile.size > maxSize) {
            const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
            setError(`Fichier trop volumineux (${fileSizeMB} Mo). Veuillez choisir un fichier de moins de 15 Mo.`);
            setFile(null);
            fileRef.current = null;
            localStorage.removeItem('selectedFile');
            return;
        }

        const fileName = (selectedFile.name || '').toLowerCase();
        const fileType = (selectedFile.type || '').toLowerCase();
        if (fileName.endsWith('.doc') && !fileName.endsWith('.docx')) {
            setError("Le format Word .doc (ancien) n'est pas supporté. Enregistrez le fichier en PDF ou en DOCX.");
            setFile(null);
            fileRef.current = null;
            localStorage.removeItem('selectedFile');
            return;
        }
        const allowedByExt = fileName.endsWith('.pdf') || fileName.endsWith('.docx');
        const allowedByType = fileType === 'application/pdf'
            || fileType === 'application/x-pdf'
            || fileType.includes('wordprocessingml');
        if (!allowedByExt && !allowedByType) {
            setError('Format non supporté. Veuillez utiliser un fichier PDF ou DOCX.');
            setFile(null);
            fileRef.current = null;
            localStorage.removeItem('selectedFile');
            return;
        }

        // Fichier valide - sauvegarder dans TOUS les endroits possibles
        setError('');
        setFile(selectedFile);
        fileRef.current = selectedFile;
        window.currentSelectedFile = selectedFile; // Variable globale
        
        // Sauvegarder les métadonnées du fichier dans localStorage
        const fileData = {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
            lastModified: selectedFile.lastModified
        };
        localStorage.setItem('selectedFile', JSON.stringify(fileData));
    }, []);


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Bloquer les re-renders pendant l'exécution
        if (window.isSubmitting) {
            return;
        }
        window.isSubmitting = true;
        
        // Désactiver la validation périodique pendant l'upload pour éviter les conflits
        window.disableTokenValidation = true;
        try {
            // VÉRIFIER le token AVANT l'upload
            const tokenCheckResponse = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/profile');

            if (!tokenCheckResponse.ok) {
                if (tokenCheckResponse.status === 401) return;
                throw new Error('Erreur lors de la vérification du token');
            }
            
            // Vérifier localStorage comme fallback
            const savedFileData = localStorage.getItem('selectedFile');
        
            // Utiliser la référence persistante au lieu de l'état
            let currentFile = fileRef.current;
        
            // Si fileRef.current est null, essayer la variable globale
            if (!currentFile && window.currentSelectedFile) {
                currentFile = window.currentSelectedFile;
                fileRef.current = currentFile;
            }
            
            // Si toujours null mais qu'on a des données dans localStorage,
            // cela signifie qu'un fichier a été sélectionné mais perdu lors du re-render
            if (!currentFile && savedFileData) {
                // Au lieu d'afficher une erreur, on va essayer de récupérer le fichier
                // depuis l'input caché
                const hiddenInput = document.getElementById('file-input-hidden');
                
                if (hiddenInput && hiddenInput.files && hiddenInput.files.length > 0) {
                    currentFile = hiddenInput.files[0];
                    fileRef.current = currentFile;
                    window.currentSelectedFile = currentFile;
                } else {
                    setError('Le fichier a été perdu lors du re-render. Veuillez le sélectionner à nouveau.');
                    return;
                }
            }
        
            if (!currentFile) {
                setError('Veuillez sélectionner un fichier CV.');
                return;
            }
            
            if (!sector && jobOffer.trim().length < 50) {
                setError('Veuillez choisir un secteur d\'activité pour une analyse personnalisée.');
                return;
            }

            if (userHasPaid && userCredits < 0.4) {
                trackCreditsInsufficient('cv_analysis');
                setInsufficientCredits(true);
                return;
            }

            setError('');
            setFeedback('');
            setScore(null);
            setScoreBreakdown(null);
            setPointsForts([]);
            setSuggestions([]);
            setMotsClesPresents([]);
            setMotsClesManquants([]);
            setIsLoading(true);
            setIsComplete(false);
            setInsufficientCredits(false);
        
            const formData = new FormData();
            formData.append('file', currentFile); // Utiliser 'file' comme attendu par la route
            formData.append('sector', sector);
            formData.append('userId', userId);
            if (jobOffer.trim().length > 50) {
                formData.append('jobOffer', jobOffer.trim());
            }
        
            // Suivi de l'upload de CV
            trackCVUpload(sector);
            
            const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/upload', {
                method: 'POST',
                headers: { 'X-User-Id': userId },
                body: formData
            });

            let result;
            try {
                result = await response.json();
            } catch {
                throw new Error("Le service d'analyse n'a pas répondu correctement. Réessayez.");
            }

            if (!response.ok) {
                if (response.status === 401) return;

                if (result.error === "Crédits insuffisants pour effectuer cette action") {
                    trackCreditsInsufficient('cv_analysis');
                    trackCvAnalysisFailed('credits', { sector });
                    setInsufficientCredits(true);
                    throw new Error("Vous n'avez pas assez de crédits pour analyser votre CV. Rechargez votre compte pour continuer.");
                }
                if (result.error && (result.error.includes("format") || result.error.includes(".doc"))) {
                    trackCvAnalysisFailed('format', { sector });
                    throw new Error(result.error);
                }
                if (result.error && (result.error.includes("taille") || result.error.includes("volumineux"))) {
                    trackCvAnalysisFailed('size', { sector });
                    throw new Error(result.error);
                }
                if (result.error && (result.error.includes("extraire") || result.error.includes("lire le texte"))) {
                    trackCvAnalysisFailed('extract', { sector });
                    throw new Error(result.error);
                }
                trackCvAnalysisFailed('api', { sector, status: response.status });
                throw new Error(result.error || "Une erreur est survenue lors de l'analyse. Veuillez réessayer.");
            }

            const feedback = result.feedback || "Aucune suggestion n'a été générée pour ce CV.";
            const score = result.score !== undefined ? result.score : 0;
            const isLimitedAnalysis = result.isLimited || false;
            const isJobMatchResult = result.isJobMatch || false;
            const cvTextFormatted = result.cvText || '';
            const analysisIdFromResult = result.id || null;
            const breakdown = normalizeScoreBreakdown(result.scoreBreakdown || null, score);
            const forts = Array.isArray(result.pointsForts) ? result.pointsForts : [];
            const sugg = Array.isArray(result.suggestions) ? result.suggestions : [];
            const presents = Array.isArray(result.motsClesPresents) ? result.motsClesPresents : [];
            const manquants = Array.isArray(result.motsClesManquants) ? result.motsClesManquants : [];

            applyPayload({
                feedback,
                score,
                analysisId: analysisIdFromResult,
                scoreBreakdown: breakdown,
                pointsForts: forts,
                suggestions: sugg,
                motsClesPresents: presents,
                motsClesManquants: manquants,
                isJobMatch: isJobMatchResult,
                cvText: isLimitedAnalysis ? '' : cvTextFormatted,
                isLimited: isLimitedAnalysis,
                sector,
                fileName: currentFile.name,
            }, { persist: true });
            setActiveTab('score');
            setReloadCreditsTrigger((n) => n + 1);
            setCompleteCv('');
            setIsFormattedCvExpanded(false);
            setIsCompleteCvExpanded(false);
            
            // Avis uniquement une fois le rapport complet vu
            const lastReviewDate = localStorage.getItem('lastReviewDate');
            const daysSinceLastReview = lastReviewDate 
                ? (Date.now() - parseInt(lastReviewDate)) / (1000 * 60 * 60 * 24)
                : Infinity;
            const prevAnalysisCount = parseInt(localStorage.getItem('analysisCount') || '0');
            const analysisCount = prevAnalysisCount + 1;
            localStorage.setItem('analysisCount', analysisCount.toString());
            
            if (!isLimitedAnalysis && daysSinceLastReview > 30 && analysisCount >= 2) {
                setTimeout(() => {
                    setShowReviewModal(true);
                }, 3000);
            }
            
            // Suivi de l'analyse complète
            trackCVAnalysis(result.score || 0, sector, {
                analysis_id: analysisIdFromResult,
                is_job_match: isJobMatchResult,
                is_limited: isLimitedAnalysis,
            });
        } catch (error) {
            setError(`Erreur lors de l'analyse: ${error.message}`);
            trackError(error.message, 'cv_upload');
            if (!error.message?.includes('crédits')) {
                trackCvAnalysisFailed('client', { message: error.message?.slice(0, 120) });
            }
        } finally {
            setIsLoading(false);
            window.isSubmitting = false; // Libérer le verrou
            window.disableTokenValidation = false; // Réactiver la validation du token
        }
    };

    // Fonction pour générer un CV complet reformaté
    const handleGenerateCompleteCv = async () => {
        if (!analysisId) {
            setError('ID d\'analyse non disponible');
            return;
        }

        setIsGeneratingCompleteCv(true);
        setError('');

        try {
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/generate-complete-cv/${analysisId}`, {
                method: 'POST',
                headers: { 'X-User-Id': userId }
            });

            const result = await response.json();

            if (!response.ok) {
                if (response.status === 401) return;
                if (result.insufficientCredits) {
                    setError(`Crédits insuffisants. ${result.requiredCredits} crédit(s) requis. Vous avez ${result.remainingCredits} crédit(s).`);
                    setInsufficientCredits(true);
                } else {
                    setError(result.error || 'Erreur lors de la génération du CV complet');
                }
                return;
            }

            if (result.success && result.completeCv) {
                setCvWithPlaceholders(result.completeCv);
                
                // Si des champs sont à compléter, afficher le formulaire
                if (result.needsCompletion && result.missingFields && result.missingFields.length > 0) {
                    setMissingFields(result.missingFields);
                    // Initialiser les valeurs des champs
                    const initialValues = {};
                    result.missingFields.forEach(field => {
                        initialValues[field.id] = '';
                    });
                    setFieldValues(initialValues);
                    setCompleteCv(''); // Pas encore de CV final
                    setCurrentFieldIndex(0); // Réinitialiser l'index
                } else {
                    // Pas de champs à compléter, CV directement disponible
                    setCompleteCv(result.completeCv);
                    setMissingFields([]);
                    setCurrentFieldIndex(0);
                }
                
                setReloadCreditsTrigger((n) => n + 1); // Recharger les crédits
                setError('CV généré avec succès !');
                setTimeout(() => setError(''), 3000);
            } else {
                setError('Erreur lors de la génération du CV complet');
            }
        } catch (error) {
            console.error('Erreur lors de la génération du CV complet:', error);
            setError('Erreur lors de la génération du CV complet');
        } finally {
            setIsGeneratingCompleteCv(false);
        }
    };

    // Fonction pour copier le CV reformaté
    const copyFormattedCv = async () => {
        try {
            await navigator.clipboard.writeText(formattedCv);
            // Afficher un message de succès temporaire
            const originalError = error;
            setError('CV reformaté copié dans le presse-papiers !');
            setTimeout(() => setError(originalError), 2000);
        } catch (err) {
            setError('Erreur lors de la copie');
        }
    };

    // Fonction pour finaliser le CV avec les informations complétées
    const handleFinalizeCv = async () => {
        if (!analysisId || missingFields.length === 0) {
            setError('Aucune information à compléter');
            return;
        }

        // Compter les champs remplis (optionnel, juste pour info)
        const filledFields = missingFields.filter(field => fieldValues[field.id] && fieldValues[field.id].trim() !== '').length;
        
        if (filledFields === 0) {
            setError('Aucun champ n\'a été rempli. Le CV sera généré avec les placeholders.');
        }

        setIsFinalizing(true);
        setError('');

        try {
            // Inclure tous les champs, même vides (pour que l'IA puisse gérer les placeholders)
            const completedFields = missingFields.map(field => ({
                id: field.id,
                value: fieldValues[field.id] || '', // Valeur vide si non rempli
                placeholder: field.placeholder
            }));

            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/finalize-complete-cv/${analysisId}`, {
                method: 'POST',
                headers: { 'X-User-Id': userId },
                body: JSON.stringify({
                    completedFields: completedFields,
                    cvWithPlaceholders: cvWithPlaceholders
                })
            });

            const result = await response.json();

            if (!response.ok) {
                setError(result.error || 'Erreur lors de la finalisation du CV');
                return;
            }

            if (result.success && result.completeCv) {
                setCompleteCv(result.completeCv);
                setMissingFields([]);
                setCvWithPlaceholders('');
                setError('CV finalisé avec succès !');
                setTimeout(() => setError(''), 3000);
            } else {
                setError('Erreur lors de la finalisation du CV');
            }
        } catch (error) {
            console.error('Erreur lors de la finalisation du CV:', error);
            setError('Erreur lors de la finalisation du CV');
        } finally {
            setIsFinalizing(false);
        }
    };

    // Fonction pour copier le CV complet
    const copyCompleteCv = async () => {
        try {
            await navigator.clipboard.writeText(completeCv);
            const originalError = error;
            setError('CV complet copié dans le presse-papiers !');
            setTimeout(() => setError(originalError), 2000);
        } catch (err) {
            setError('Erreur lors de la copie');
        }
    };

    // Fonction pour télécharger le CV reformaté en TXT
    const downloadFormattedCvTxt = () => {
        const blob = new Blob([formattedCv], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV-reformate-${file?.name?.replace(/\.[^/.]+$/, '') || 'cv'}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Fonction pour télécharger le CV reformaté en DOCX (via conversion simple)
    const downloadFormattedCvDocx = () => {
        // Pour DOCX, on utilise une conversion simple en HTML puis téléchargement
        // Note: Pour un vrai DOCX, il faudrait utiliser une librairie comme docx
        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
        h2 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 5px; }
        h3 { color: #1e40af; margin-top: 20px; }
        p { margin: 5px 0; }
    </style>
</head>
<body>
    <pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${formattedCv}</pre>
</body>
</html>`;
        const blob = new Blob([htmlContent], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV-reformate-${file?.name?.replace(/\.[^/.]+$/, '') || 'cv'}.doc`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const resetForm = () => {
        skipRestoreRef.current = true;
        setFile(null);
        setSector('');
        setFeedback('');
        setScore(null);
            setScoreBreakdown(null);
            setPointsForts([]);
            setSuggestions([]);
            setMotsClesPresents([]);
            setMotsClesManquants([]);
            setError('');
        setIsComplete(false);
        setInsufficientCredits(false);
        setIsLimited(false);
        setFormattedCv('');
        setAnalysisId(null);
        setCompleteCv('');
        setIsGeneratingCompleteCv(false);
        setMissingFields([]);
        setFieldValues({});
        setIsFinalizing(false);
        setCvWithPlaceholders('');
        setCurrentFieldIndex(0);
        setJobOffer('');
        setIsJobMatch(false);
        setShowAllSuggestions(false);
        setIsFormattedCvExpanded(false);
        setIsCompleteCvExpanded(false);
        // Reset file input
        const fileInput = document.getElementById('file-input-hidden');
        if (fileInput) fileInput.value = '';
        fileRef.current = null;
        window.currentSelectedFile = null;
        localStorage.removeItem('selectedFile');
        localStorage.removeItem('lastAnalysis');
    };

    const displayedSuggestions = showAllSuggestions ? suggestions : suggestions.slice(0, 4);
    const hasMoreSuggestions = suggestions.length > 4;
    const isFormattedCvLong = formattedCv && (formattedCv.length > 1800 || formattedCv.split('\n').length > 40);
    const isCompleteCvLong = completeCv && (completeCv.length > 1800 || completeCv.split('\n').length > 40);

    if (insufficientCredits) {
        return (
            <div className="page-shell flex items-center justify-center p-4">
                <div className="card-surface p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ExclamationTriangleIcon className="w-10 h-10 text-yellow-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Crédits insuffisants</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        Vous n'avez pas assez de crédits pour analyser votre CV. Rechargez votre compte pour continuer.
                    </p>
                    <Link 
                        to="/subscription"
                        onClick={() => trackSubscriptionCtaClick('insufficient_credits_screen')}
                        className="inline-block bg-blue-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                        Recharger mon compte
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell overflow-x-hidden px-4">
            {showOnboarding && (
                <OnboardingModal onClose={handleCloseOnboarding} />
            )}
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1 className="page-title">
                            Analyse de CV
                        </h1>
                        <p className="page-subtitle">
                            Importez un PDF ou un Word. Score et conseils en moins de 2 minutes.
                        </p>
                    </div>
                </div>

                {/* Credits Display */}
                <div className="flex justify-center mb-8">
                    <UserCredits reloadTrigger={reloadCreditsTrigger} />
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Upload Form — remplacé par un résumé + bouton "Nouvelle analyse" si résultats affichés */}
                    <div className="card-surface p-4 sm:p-8 min-w-0">
                        {isComplete ? (
                            <div className="flex flex-col items-center justify-center h-full text-center gap-6 py-8">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800 mb-2">Prochaine étape</h2>
                                    <p className="text-gray-500 text-sm">
                                        {file?.name && <span className="font-medium text-gray-700 break-all">{file.name}</span>}
                                        {sector && !isJobMatch && <span className="block text-gray-400 mt-1">Secteur : {sector}</span>}
                                        {isJobMatch && <span className="block text-gray-400 mt-1">Mode matching offre d&apos;emploi</span>}
                                    </p>
                                    <p className="text-gray-600 text-sm mt-3">
                                        Appliquez les conseils à droite, enregistrez une nouvelle version de votre CV, puis relancez une analyse pour voir si le score progresse.
                                    </p>
                                </div>
                                {!userHasPaid || userCredits >= 0.4 ? (
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="btn-primary"
                                    >
                                        Ré-analyser mon CV
                                    </button>
                                ) : (
                                    <Link
                                        to="/subscription"
                                        className="btn-primary"
                                    >
                                        Recharger pour ré-analyser
                                    </Link>
                                )}
                                <Link
                                    to="/history"
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2 transition-colors"
                                >
                                    📋 Voir toutes mes analyses
                                </Link>
                            </div>
                        ) : (
                        <>
                        <div className="flex items-center mb-8">
                            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                                <CloudArrowUpIcon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Télécharger votre CV</h2>
                        </div>

                            {/* Bandeau crédits faibles */}
                            {userHasPaid && userCredits > 0 && userCredits < 1 && (
                                <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-amber-500 text-lg">⚠️</span>
                                        <p className="text-sm text-amber-800">
                                            Il vous reste <strong>{Math.floor(userCredits / 0.4)} analyse{Math.floor(userCredits / 0.4) > 1 ? 's' : ''}</strong> sur votre solde.
                                        </p>
                                    </div>
                                    <a
                                        href="/subscription"
                                        className="flex-shrink-0 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                        Recharger →
                                    </a>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* File Upload - Solution fonctionnelle */}
                                <div>
                                    <label htmlFor="file-input-hidden" className="block text-sm font-semibold text-gray-700 mb-3">
                                        Fichier CV (PDF ou DOCX)
                                    </label>
                                    
                                    {/* Bouton pour déclencher la sélection de fichier */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            document.getElementById('file-input-hidden').click();
                                        }}
                                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                                    >
                                        <div className="text-center">
                                            <CloudArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-gray-600 font-medium">Cliquer pour sélectionner un fichier</p>
                                            <p className="text-sm text-gray-500 mt-1">PDF ou DOCX • Taille maximale : 15 Mo</p>
                                        </div>
                                    </button>
                                    
                                    {/* Input de fichier caché avec event listener natif */}
                                    <input
                                        id="file-input-hidden"
                                        type="file"
                                        accept=".pdf,.docx"
                                        style={{ display: 'none' }}
                                        ref={(input) => {
                                            if (input) {
                                                // Ajouter un event listener natif
                                                input.addEventListener('change', (e) => {
                                                    const selectedFile = e.target.files[0];
                                                    if (selectedFile) {
                                                        processFile(selectedFile);
                                                    }
                                                });
                                            }
                                        }}
                                    />
                                
                                {/* Affichage du fichier sélectionné */}
                                {file && (
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                                        <div className="flex items-center mb-2">
                                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                                            <span className="text-green-800 font-medium">Fichier sélectionné</span>
                                        </div>
                                        <p className="text-green-700">{file.name} — {(file.size / 1024).toFixed(1)} Ko</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFile(null);
                                                fileRef.current = null;
                                                window.currentSelectedFile = null;
                                                const input = document.getElementById('file-input-hidden');
                                                if (input) input.value = '';
                                            }}
                                            className="mt-2 text-red-500 hover:text-red-700 text-sm font-medium"
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Sector Selection — masqué si une offre est collée */}
                            {jobOffer.trim().length < 50 && (
                            <div>
                                <label htmlFor="sector-select" className="block text-sm font-semibold text-gray-700 mb-3">
                                    🎯 Secteur d'activité
                                </label>
                                <select
                                    id="sector-select"
                                    value={sector}
                                    onChange={(e) => setSector(e.target.value)}
                                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                                >
                                    <option value="">🎯 Choisissez votre secteur d'activité</option>
                                    {sectors.map((sectorOption) => (
                                        <option key={sectorOption.value} value={sectorOption.value}>
                                            {sectorOption.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            )}

                            {/* Offre d'emploi : option, pas le parcours principal */}
                            <div className={`rounded-xl border p-4 ${jobOffer.trim().length >= 50 ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-white'}`}>
                                <div className="mb-2">
                                    <span className="font-semibold text-gray-800 text-sm">Option : coller une offre d&apos;emploi</span>
                                </div>
                                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                                    Pour un score de compatibilité avec un poste précis (après le rapport, ou si vous avez déjà une offre).
                                </p>
                                <textarea
                                    id="job-offer-input"
                                    value={jobOffer}
                                    onChange={(e) => setJobOffer(e.target.value)}
                                    rows={4}
                                    placeholder="Collez ici le texte de l'offre d'emploi (LinkedIn, Indeed, Welcome to the Jungle…)"
                                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm resize-y bg-white ${jobOffer.trim().length >= 50 ? 'border-slate-400' : 'border-slate-200'}`}
                                />
                                {jobOffer.trim().length > 0 && jobOffer.trim().length < 50 && (
                                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">⚠️ Ajoutez un peu plus de texte pour activer la comparaison.</p>
                                )}
                                {jobOffer.trim().length >= 50 && (
                                    <p className="text-xs text-purple-700 font-semibold mt-2 flex items-center gap-1">✅ Matching activé — analyse adaptée à cette offre</p>
                                )}
                                {jobOffer.trim().length === 0 && (
                                    <p className="text-xs text-gray-400 mt-2">Sans offre, l'analyse reste générale selon le secteur choisi.</p>
                                )}
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                    <div className="flex items-start">
                                        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                                        <p className="text-red-600 text-sm">{error}</p>
                                    </div>
                                    {file && (
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            className="mt-3 text-sm font-medium text-red-700 underline"
                                        >
                                            Réessayer avec le même fichier
                                        </button>
                                    )}
                                </div>
                            )}

                            <p className="text-xs text-gray-400 text-center">
                                {userHasPaid
                                    ? 'Chaque relance utilise vos crédits.'
                                    : 'L’aperçu (score + 3 conseils) est offert, sans IA. Après paiement, réimportez le CV pour le rapport complet.'}
                            </p>

                            {userHasPaid && userCredits < 0.4 && (
                                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-amber-800 font-semibold text-sm">
                                            Crédits insuffisants
                                        </p>
                                        <p className="text-amber-700 text-xs mt-0.5">
                                            Rechargez pour relancer une analyse.
                                        </p>
                                        <Link
                                            to="/subscription"
                                            onClick={() => trackSubscriptionCtaClick('upload_low_credits')}
                                            className="inline-block mt-2 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            Recharger →
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || (userHasPaid && userCredits < 0.4)}
                                className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                                    isLoading || (userHasPaid && userCredits < 0.4)
                                        ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                                }`}
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                        Analyse en cours...
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center">
                                        <SparklesIcon className="w-5 h-5 mr-2" />
                                        {jobOffer.trim().length >= 50 ? 'Analyser la compatibilité' : 'Analyser mon CV'}
                                    </div>
                                )}
                            </button>
                        </form>
                        </>
                        )}
                    </div>

                    {/* Results */}
                    <div className="card-surface p-4 sm:p-8 min-w-0">
                        <div className="flex items-center mb-6">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-3">
                                <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Résultats de l'analyse</h2>
                        </div>

                        {!isComplete && !isLoading && (
                            <div className="text-center py-16">
                                <DocumentTextIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                                <p className="text-gray-500 text-lg">
                                    Téléchargez votre CV pour voir les résultats d'analyse
                                </p>
                                <p className="text-gray-400 text-sm mt-2">
                                    Notre IA analysera votre CV et vous donnera des conseils personnalisés
                                </p>
                            </div>
                        )}

                        {isLoading && <AnalysisProgressBar isJobMatch={isJobMatch} />}

                        {isComplete && (
                            <div>
                                {/* Onglets */}
                                <div className="flex bg-gray-100 rounded-2xl p-1 mb-6 gap-1">
                                    <button
                                        onClick={() => setActiveTab('score')}
                                        className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2.5 px-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${activeTab === 'score' ? 'bg-white shadow-md text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <ChartBarIcon className="w-4 h-4 shrink-0" />
                                        <span>Score</span>
                                        {score !== null && (
                                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${score >= 70 ? 'bg-green-100 text-green-700' : score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                                {score}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('suggestions')}
                                        className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2.5 px-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${activeTab === 'suggestions' ? 'bg-white shadow-md text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <SparklesIcon className="w-4 h-4 shrink-0" />
                                        <span>Conseils</span>
                                        {suggestions.length > 0 && (
                                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">{suggestions.length}</span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('cv')}
                                        className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2.5 px-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${activeTab === 'cv' ? 'bg-white shadow-md text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <DocumentTextIcon className="w-4 h-4 shrink-0" />
                                        <span>CV & Lettre</span>
                                    </button>
                                </div>

                                {!isLimited && (
                                    <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                                        Rapport complet : détail par critère, toutes les recommandations, mots-clés ATS et CV restructuré.
                                    </div>
                                )}

                                <div className="space-y-6">

                                {/* ── ONGLET SCORE ── */}
                                {activeTab === 'score' && <>
                                {/* Score */}
                                {score !== null && (
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-6 border border-blue-100">
                                        <div className="flex items-start justify-between mb-4 gap-2 min-w-0">
                                            <h3 className="text-base sm:text-xl font-semibold text-gray-800 min-w-0 break-words">
                                                {isJobMatch ? 'Compatibilité avec l\'offre' : 'Note globale'}
                                            </h3>
                                            <span className="text-2xl sm:text-3xl font-bold bg-blue-600 bg-clip-text text-transparent shrink-0 ml-2">
                                                {score}/100
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div
                                                className="bg-gradient-to-r from-blue-500 to-purple-600 h-4 rounded-full transition-all duration-1000 ease-out"
                                                style={{ width: `${score}%` }}
                                            ></div>
                                        </div>
                                        <div className="mt-3 text-sm text-gray-600">
                                            {isJobMatch ? (
                                                score >= 80 ? 'Excellent match ! Votre profil correspond très bien à cette offre.' :
                                                score >= 60 ? 'Bon match ! Quelques ajustements pour renforcer votre candidature.' :
                                                score >= 40 ? 'Match partiel. Des adaptations importantes sont recommandées.' :
                                                'Profil éloigné de l\'offre. Consultez les suggestions pour améliorer votre candidature.'
                                            ) : (
                                                score >= 80 ? 'Excellent ! Votre CV est très bien structuré.' :
                                                score >= 60 ? 'Bon travail ! Quelques améliorations possibles.' :
                                                score >= 40 ? 'Correct, mais des améliorations importantes sont nécessaires.' :
                                                'Votre CV nécessite une refonte complète.'
                                            )}
                                        </div>
                                    </div>
                                )}

                                {score !== null && isLimited && (
                                    <UnlockReportCta title="Votre score est un aperçu" hasPaid={userHasPaid} onReanalyze={resetForm} />
                                )}

                                {score !== null && !isLimited && (
                                    <div className="bg-indigo-50 rounded-2xl p-4 sm:p-5 border border-indigo-100">
                                        <p className="text-sm font-semibold text-gray-800 mb-1">Faites progresser ce score</p>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Appliquez les conseils, puis relancez une analyse avec le CV mis à jour. Chaque relance utilise vos crédits.
                                        </p>
                                        {userCredits >= 0.4 ? (
                                            <button
                                                type="button"
                                                onClick={resetForm}
                                                className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg"
                                            >
                                                Ré-analyser mon CV
                                            </button>
                                        ) : (
                                            <Link
                                                to="/subscription"
                                                className="inline-block text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg"
                                            >
                                                Recharger pour ré-analyser
                                            </Link>
                                        )}
                                    </div>
                                )}

                                {/* Score détaillé + Points forts — masqués tant que le rapport n'est pas payé */}
                                {!isLimited && ((displayScoreBreakdown && typeof displayScoreBreakdown === 'object') || pointsForts.length > 0) ? (
                                    <div className="relative">
                                        <div>
                                            {displayScoreBreakdown && typeof displayScoreBreakdown === 'object' && (
                                                <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 mb-4">
                                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                                        <ChartBarIcon className="w-5 h-5 mr-2 text-indigo-600" />
                                                        Détail du score
                                                    </h3>
                                                    <div className="space-y-3">
                                                        {(isJobMatch ? [
                                                            { key: 'requirements', label: 'Exigences requises' },
                                                            { key: 'experience', label: 'Expériences' },
                                                            { key: 'skills', label: 'Compétences' },
                                                            { key: 'keywords', label: 'Mots-clés offre' },
                                                            { key: 'presentation', label: 'Présentation' }
                                                        ] : [
                                                            { key: 'structure', label: 'Structure' },
                                                            { key: 'clarte', label: 'Clarté' },
                                                            { key: 'motsCles', label: 'Mots-clés' },
                                                            { key: 'secteur', label: 'Secteur' },
                                                            { key: 'quantification', label: 'Chiffres' }
                                                        ]).filter(({ key }) => displayScoreBreakdown[key] != null).map(({ key, label }) => (
                                                            <div key={key}>
                                                                <div className="flex justify-between text-sm mb-1">
                                                                    <span className="text-gray-700">{label}</span>
                                                                    <span className="font-medium text-gray-800">{displayScoreBreakdown[key]}/100</span>
                                                                </div>
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                                                                        style={{ width: `${Math.min(100, Math.max(0, displayScoreBreakdown[key]))}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {pointsForts.length > 0 && (
                                                <div className="bg-green-50 rounded-2xl p-4 sm:p-6 border border-green-100">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                                        <CheckCircleIcon className="w-5 h-5 mr-2 text-green-600" />
                                                        {isJobMatch ? 'Ce qui correspond à l\'offre' : 'Points forts'}
                                                    </h3>
                                                    <ul className="space-y-2">
                                                        {pointsForts.map((point, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-gray-700">
                                                                <span className="text-green-600 mt-0.5">•</span>
                                                                <span>{point}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                                </>}

                                {/* ── ONGLET SUGGESTIONS ── */}
                                {activeTab === 'suggestions' && <>
                                {/* Suggestions */}
                                <div className="relative">
                                <div>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                        <SparklesIcon className="w-5 h-5 mr-2 text-blue-600" />
                                        {isJobMatch ? 'Comment adapter votre CV à cette offre' : 'Suggestions d\'amélioration'}
                                        {isLimited && (
                                            <span className="ml-2 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                                                Version limitée
                                            </span>
                                        )}
                                    </h3>
                                    {suggestions.length > 0 ? (
                                        <div className="space-y-4">
                                            {displayedSuggestions.map((s, i) => (
                                                <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        {s.titre && <span className="font-semibold text-gray-800">{s.titre}</span>}
                                                        {s.priorite && (
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                s.priorite === 'haute' ? 'bg-red-100 text-red-800' :
                                                                s.priorite === 'moyenne' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {s.priorite}
                                                            </span>
                                                        )}
                                                        {s.categorie && (
                                                            <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{s.categorie}</span>
                                                        )}
                                                    </div>
                                                    {s.description && <p className="text-sm text-gray-700 leading-relaxed">{s.description}</p>}
                                                </div>
                                            ))}
                                            {hasMoreSuggestions && (
                                                <button
                                                    onClick={() => setShowAllSuggestions(!showAllSuggestions)}
                                                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline"
                                                >
                                                    {showAllSuggestions
                                                        ? 'Voir moins de suggestions'
                                                        : `Voir toutes les suggestions (${suggestions.length})`}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                                            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed max-h-72 overflow-y-auto pr-2">
                                                {feedback}
                                            </pre>
                                        </div>
                                    )}
                                    
                                    {isLimited && (
                                        <div className="mt-6">
                                            <UnlockReportCta title="Les autres conseils sont dans le rapport complet" hasPaid={userHasPaid} onReanalyze={resetForm} />
                                        </div>
                                    )}
                                </div>

                                {/* Mots-clés et ATS */}
                                {(motsClesPresents.length > 0 || motsClesManquants.length > 0) && (
                                    <div className="bg-amber-50 rounded-2xl p-4 sm:p-6 border border-amber-100">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                            <ChartBarIcon className="w-5 h-5 mr-2 text-amber-600" />
                                            {isJobMatch ? 'Mots-clés de l\'offre' : 'Mots-clés et ATS'}
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-4">
                                            {isJobMatch
                                                ? 'Termes importants de l\'offre d\'emploi retrouvés ou manquants dans votre CV.'
                                                : 'Ces termes améliorent votre visibilité auprès des logiciels de tri (ATS) et des recruteurs du secteur.'}
                                        </p>
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            {motsClesPresents.length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-green-800 mb-2">{isJobMatch ? 'Présents dans votre CV' : 'Présents dans votre CV'}</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {motsClesPresents.map((mot, i) => (
                                                            <span key={i} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                                {mot}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {motsClesManquants.length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-amber-800 mb-2">{isJobMatch ? 'Absents — à ajouter en priorité' : 'À renforcer ou ajouter'}</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {motsClesManquants.map((mot, i) => (
                                                            <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                                                                {mot}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                </div>{/* fin relative wrapper */}
                                </>}

                                {/* ── ONGLET CV & LETTRE ── */}
                                {activeTab === 'cv' && <>
                                {isLimited && (
                                    <UnlockReportCta title="CV restructuré et lettre inclus dans le rapport" hasPaid={userHasPaid} onReanalyze={resetForm} />
                                )}
                                {/* CTA Lettre de motivation — uniquement si l'analyse est sauvegardée en base */}
                                {!isLimited && analysisId && (
                                    <div className={`rounded-2xl p-6 border ${isJobMatch ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                                    <SparklesIcon className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-800">
                                                        {isJobMatch ? 'Générer ma lettre pour ce poste' : 'Générer ma lettre de motivation'}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mt-0.5">
                                                        {isJobMatch
                                                            ? 'Lettre personnalisée basée sur votre CV et l\'offre analysée — 7 crédits'
                                                            : 'Lettre professionnelle générée par IA à partir de votre CV — 7 crédits'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowUpsellModal(true)}
                                                className="flex-shrink-0 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg text-sm"
                                            >
                                                Générer la lettre
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Données extraites et structurées */}
                                {formattedCv && !isLimited && (
                                    <div className="mt-8">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2 mb-4">
                                            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                                                <DocumentTextIcon className="w-5 h-5 mr-2 text-blue-600" />
                                                Données extraites et structurées
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={copyFormattedCv}
                                                    className="px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2 text-xs sm:text-sm font-medium"
                                                    title="Copier les données structurées"
                                                >
                                                    <ClipboardDocumentIcon className="w-4 h-4" />
                                                    Copier
                                                </button>
                                                <button
                                                    onClick={downloadFormattedCvTxt}
                                                    className="px-3 sm:px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2 text-xs sm:text-sm font-medium"
                                                    title="Télécharger en TXT"
                                                >
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                    TXT
                                                </button>
                                                <button
                                                    onClick={downloadFormattedCvDocx}
                                                    className="px-3 sm:px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-2 text-xs sm:text-sm font-medium"
                                                    title="Télécharger en DOCX"
                                                >
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                    DOC
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border-2 border-blue-200">
                                            <pre className={`whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed pr-2 ${
                                                isFormattedCvLong && !isFormattedCvExpanded ? 'max-h-72 overflow-y-auto' : ''
                                            }`}>
                                                {formattedCv}
                                            </pre>
                                        </div>
                                        {isFormattedCvLong && (
                                            <button
                                                onClick={() => setIsFormattedCvExpanded(!isFormattedCvExpanded)}
                                                className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700 underline"
                                            >
                                                {isFormattedCvExpanded ? 'Reduire l’affichage' : 'Afficher le texte complet'}
                                            </button>
                                        )}
                                        <p className="text-xs text-gray-500 mt-2">
                                            💡 Données extraites et structurées de votre CV.
                                        </p>
                                    </div>
                                )}

                                {/* Bouton pour générer le CV complet — après paiement uniquement */}
                                {!isLimited && analysisId && !completeCv && missingFields.length === 0 && (
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 border-2 border-blue-200">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-gray-800 mb-1">
                                                    📄 Générer un CV complet reformaté
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    Obtenez un CV complet et professionnel avec toutes vos informations (2 crédits)
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleGenerateCompleteCv}
                                                disabled={isGeneratingCompleteCv}
                                                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {isGeneratingCompleteCv ? (
                                                    <>
                                                        <span className="animate-spin">⏳</span>
                                                        Génération...
                                                    </>
                                                ) : (
                                                    <>Générer (2 crédits)</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* CV avec placeholders - Formulaire de complétion */}
                                {cvWithPlaceholders && missingFields.length > 0 && !completeCv && (
                                    <div className="mt-8">
                                        <div className="mb-4">
                                            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                                <span className="flex items-center">
                                                    <DocumentTextIcon className="w-5 h-5 mr-2 text-yellow-600" />
                                                    CV généré - Informations à compléter
                                                </span>
                                                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full inline-block w-fit">
                                                    {currentFieldIndex + 1} / {missingFields.length}
                                                </span>
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-4">
                                                Complétez les informations manquantes pour finaliser votre CV. Utilisez les boutons pour naviguer entre les champs.
                                            </p>
                                        </div>

                                        {/* Barre de progression */}
                                        <div className="mb-4">
                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                                <div 
                                                    className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${((currentFieldIndex + 1) / missingFields.length) * 100}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-xs text-gray-600 text-center">
                                                {Math.round(((currentFieldIndex + 1) / missingFields.length) * 100)}% complété
                                            </p>
                                        </div>

                                        {/* Formulaire de complétion avec navigation */}
                                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 sm:p-6 border-2 border-yellow-200">
                                            {missingFields[currentFieldIndex] && (
                                                <>
                                                    <div className="bg-white rounded-lg p-4 sm:p-6 border-2 border-yellow-300 shadow-lg">
                                                        <div className="mb-4">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <label className="text-base sm:text-lg font-semibold text-gray-800">
                                                                    {missingFields[currentFieldIndex].label}
                                                                </label>
                                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                                                    {missingFields[currentFieldIndex].section}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-600">
                                                                {missingFields[currentFieldIndex].context}
                                                            </p>
                                                        </div>
                                                        
                                                        {missingFields[currentFieldIndex].type === 'date' ? (
                                                            <input
                                                                ref={currentFieldRef}
                                                                type="text"
                                                                placeholder="Ex: Janvier 2020 - Décembre 2022"
                                                                value={fieldValues[missingFields[currentFieldIndex].id] || ''}
                                                                onChange={(e) => setFieldValues({ ...fieldValues, [missingFields[currentFieldIndex].id]: e.target.value })}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && fieldValues[missingFields[currentFieldIndex].id]) {
                                                                        if (currentFieldIndex < missingFields.length - 1) {
                                                                            setCurrentFieldIndex(currentFieldIndex + 1);
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                                            />
                                                        ) : (
                                                            <textarea
                                                                ref={currentFieldRef}
                                                                placeholder={`Saisissez ${missingFields[currentFieldIndex].label.toLowerCase()}`}
                                                                value={fieldValues[missingFields[currentFieldIndex].id] || ''}
                                                                onChange={(e) => setFieldValues({ ...fieldValues, [missingFields[currentFieldIndex].id]: e.target.value })}
                                                                rows={missingFields[currentFieldIndex].type === 'description' ? 5 : 3}
                                                                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-y"
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Navigation */}
                                                    <div className="flex items-center justify-between mt-4 gap-3">
                                                        <button
                                                            onClick={() => setCurrentFieldIndex(Math.max(0, currentFieldIndex - 1))}
                                                            disabled={currentFieldIndex === 0}
                                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                        >
                                                            ← Précédent
                                                        </button>
                                                        
                                                        <span className="text-sm text-gray-600 text-center flex-1">
                                                            Champ {currentFieldIndex + 1} sur {missingFields.length}
                                                            {(!fieldValues[missingFields[currentFieldIndex].id] || fieldValues[missingFields[currentFieldIndex].id].trim() === '') && (
                                                                <span className="block text-xs text-gray-500 mt-1">(Optionnel)</span>
                                                            )}
                                                        </span>
                                                        
                                                        {currentFieldIndex < missingFields.length - 1 ? (
                                                            <button
                                                                onClick={() => setCurrentFieldIndex(currentFieldIndex + 1)}
                                                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
                                                            >
                                                                {fieldValues[missingFields[currentFieldIndex].id] && fieldValues[missingFields[currentFieldIndex].id].trim() !== '' ? (
                                                                    <>Suivant →</>
                                                                ) : (
                                                                    <>Passer →</>
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={handleFinalizeCv}
                                                                disabled={isFinalizing}
                                                                className="px-6 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-semibold rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                            >
                                                                {isFinalizing ? (
                                                                    <>
                                                                        <span className="animate-spin">⏳</span>
                                                                        Finalisation...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        ✅ Finaliser le CV
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* CV Complet généré */}
                                {completeCv && (
                                    <div className="mt-8">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2 mb-4">
                                            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 flex flex-col sm:flex-row sm:items-center gap-2">
                                                <span className="flex items-center">
                                                    <DocumentTextIcon className="w-5 h-5 mr-2 text-green-600" />
                                                    Votre CV complet reformaté
                                                </span>
                                                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full inline-block w-fit">
                                                    ✓ Généré
                                                </span>
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={copyCompleteCv}
                                                    className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    title="Copier le CV complet"
                                                >
                                                    <ClipboardDocumentIcon className="w-4 h-4" />
                                                    Copier
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const blob = new Blob([completeCv], { type: 'text/plain;charset=utf-8' });
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `CV-complet-${file?.name?.replace(/\.[^/.]+$/, '') || 'cv'}.txt`;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        document.body.removeChild(a);
                                                        URL.revokeObjectURL(url);
                                                    }}
                                                    className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    title="Télécharger en TXT"
                                                >
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                    TXT
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border-2 border-green-200">
                                            <pre className={`whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed pr-2 ${
                                                isCompleteCvLong && !isCompleteCvExpanded ? 'max-h-72 overflow-y-auto' : ''
                                            }`}>
                                                {completeCv}
                                            </pre>
                                        </div>
                                        {isCompleteCvLong && (
                                            <button
                                                onClick={() => setIsCompleteCvExpanded(!isCompleteCvExpanded)}
                                                className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700 underline"
                                            >
                                                {isCompleteCvExpanded ? 'Reduire l’affichage' : 'Afficher le texte complet'}
                                            </button>
                                        )}
                                        <p className="text-xs text-gray-500 mt-2">
                                            ✅ CV complet généré avec toutes vos informations. Vous pouvez le copier ou le télécharger pour l'utiliser directement.
                                        </p>
                                    </div>
                                )}

                                </>}

                                </div>{/* end space-y-6 */}

                                {/* Reset Button — toujours visible */}
                                <button
                                    onClick={resetForm}
                                    className="w-full mt-6 py-4 px-6 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                                >
                                    Analyser un autre CV
                                </button>
                                <Link
                                    to="/history"
                                    className="block text-center text-sm text-gray-500 hover:text-blue-600 font-medium mt-3 transition-colors"
                                >
                                    📋 Voir toutes mes analyses précédentes
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Assistant IA */}
            {!isLimited && <AiAssistantButton sector={sector} />}

            {/* Modal de Review */}
            {showReviewModal && (
                <ModalReview 
                    onClose={() => {
                        setShowReviewModal(false);
                    }}
                    score={score}
                />
            )}

            {/* Modal d'Upsell */}
            {showUpsellModal && (
                <ModalUpsell
                    isOpen={showUpsellModal}
                    onClose={() => {
                        setShowUpsellModal(false);
                    }}
                    analysisId={analysisId}
                    score={score}
                    sector={sector}
                />
            )}
        </div>
    );
};

export default CvUploader;
